import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "../../components/ui/index.jsx";
import { useAuth } from "../../context/AuthContext";
import { useFinanceOverview } from "../../hooks/useAssets";
import { formatIDR } from "../../utils/format";
import { cn } from "../../utils/cn";
import { TrendChart } from "../../components/charts/TrendChart.jsx";
import { StackedBreakdown } from "../../components/charts/StackedBreakdown.jsx";

/**
 * Finance (CONTEXT.md "Asset Summary", across entities).
 *
 * The one question: which entity holds how much value, and what is about to
 * change? The entity table answers it, the installment months show what rolls
 * off, and the footer of the table says how much of the register the totals do
 * not cover. Figures in ink; the one blue is on the installments that end.
 * Cost, book value and % depreciated are measured at the end of the chosen
 * month; every count is current and says so.
 */

const CAN_SEE_FINANCE = ["ADMIN", "GA", "FINANCE", "EXECUTIVE"];

// The server's current month is a UTC month, so the picker's upper bound is too
const currentPeriod = () => new Date().toISOString().slice(0, 7);

// All dates here are UTC calendar dates; format them as such so a month-end
// never reads as the 1st of the next month on a machine ahead of UTC.
const utc = (d, opts) => new Date(d).toLocaleDateString("en-GB", { timeZone: "UTC", ...opts });
// Fixed names: Intl renders September as "Sept" in en-GB, which breaks the column
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatMonth = (ym) => `${MONTH_NAMES[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

const num = (n) => Number(n ?? 0).toLocaleString("id-ID");
const pct = (n) =>
  n === null || n === undefined ? <span className="text-slate-300">—</span> : `${String(n).replace(".", ",")}%`;

function Zero({ children }) {
  return <span className="text-slate-300">{children}</span>;
}

// A count that opens the matching, pre-filtered asset list when there is
// something to open; a zero is printed as a grey zero, not a link.
function CountLink({ value, to }) {
  if (!value) return <Zero>0</Zero>;
  return (
    <Link
      to={to}
      onClick={(e) => e.stopPropagation()}
      className="text-slate-900 hover:underline underline-offset-2 tabular-nums"
    >
      {num(value)}
    </Link>
  );
}

function listLink(entityId, period, extra = {}) {
  const p = new URLSearchParams();
  if (entityId) p.set("entityId", entityId);
  if (period && period !== currentPeriod()) p.set("period", period);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const qs = p.toString();
  return `/assets${qs ? `?${qs}` : ""}`;
}

// The finding, said once under the title: who holds the value, what ends, what
// is missing. Built from the same data as the blocks below, never typed apart.
function finding(data) {
  const parts = [];
  const top = data.entities[0];
  if (top && data.entities.length > 1 && data.total.bookValue > 0) {
    const share = Math.round((top.bookValue / data.total.bookValue) * 100);
    parts.push(`${top.code} holds ${share}% of book value`);
  }
  const endingSoon = data.installments.months.reduce((s, m) => s + m.count, 0);
  if (endingSoon > 0) {
    parts.push(`${num(endingSoon)} ${endingSoon === 1 ? "installment ends" : "installments end"} within 12 months`);
  }
  const nv = data.completeness.notValued.count;
  if (nv > 0) parts.push(`${num(nv)} ${nv === 1 ? "asset" : "assets"} not valued`);
  return parts.join(" · ");
}

// Below `xl` only Entity, cost and book value are shown: the sidebar leaves a
// tablet or small laptop too little width, and the secondary columns would push
// the book value off screen. They are one tap away (each entity row opens its
// asset list).
const SECONDARY = "hidden xl:table-cell";

function Th({ children, right, hint, secondary }) {
  return (
    <th
      scope="col"
      title={hint}
      className={cn(
        "px-2 sm:px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap",
        right ? "text-right" : "text-left",
        secondary && SECONDARY,
      )}
    >
      {children}
    </th>
  );
}

function EntitiesCard({ data, period }) {
  const { entities, total, completeness } = data;
  const asOf = `as of ${utc(data.asOf, { day: "2-digit", month: "short", year: "numeric" })}`;
  const nv = completeness.notValued;

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 px-5 pt-4 pb-3">
        <h2 className="text-sm font-semibold text-slate-900">Entities</h2>
        <span className="text-xs text-slate-400">
          Cost, book value and depreciated {asOf} · counts as of today
        </span>
      </div>

      {entities.length === 0 ? (
        <p className="px-5 pb-8 pt-4 text-sm text-slate-400 text-center">No assets yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-slate-200 bg-slate-50">
                <Th>Entity</Th>
                <Th right secondary>Assets</Th>
                <Th right>
                  <span className="lg:hidden">Cost</span>
                  <span className="hidden lg:inline">Acquisition cost</span>
                </Th>
                <Th right>Book value</Th>
                <Th right secondary hint="Share of acquisition cost already depreciated, over the assets that have a price and a useful life">
                  Depreciated
                </Th>
                <Th right secondary hint="Installment plans whose end date has not passed">Under installment</Th>
                <Th right secondary hint="Assets with at least one data-issue flag">Flagged</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entities.map((e) => (
                <tr key={e.entityId} className="hover:bg-slate-50">
                  <td className="px-2 sm:px-4 py-3">
                    <Link
                      to={listLink(e.entityId, period)}
                      className="flex items-center gap-2.5 min-w-0"
                      title={`Open ${e.name}'s assets`}
                    >
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded shrink-0">
                        {e.code}
                      </span>
                      <span className="text-slate-900 truncate hidden lg:inline">{e.name}</span>
                    </Link>
                  </td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right tabular-nums">
                    <CountLink value={e.assets} to={listLink(e.entityId, period)} />
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-right text-slate-900 tabular-nums whitespace-nowrap text-[13px] sm:text-sm">
                    {formatIDR(e.cost)}
                  </td>
                  <td className="px-2 sm:px-4 py-3 text-right text-slate-900 tabular-nums whitespace-nowrap text-[13px] sm:text-sm">
                    {formatIDR(e.bookValue)}
                  </td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right text-slate-600 tabular-nums">{pct(e.depreciatedPct)}</td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right tabular-nums">
                    <CountLink
                      value={e.underInstallment}
                      to={listLink(e.entityId, period, { ownership: "UNDER_INSTALLMENT" })}
                    />
                  </td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right tabular-nums">
                    <CountLink value={e.flagged} to={listLink(e.entityId, period, { dataIssue: "ANY" })} />
                  </td>
                </tr>
              ))}
            </tbody>
            {entities.length > 1 && (
              <tfoot>
                <tr className="border-t border-slate-300">
                  <td className="px-2 sm:px-4 py-3 text-slate-900 font-medium whitespace-nowrap">
                    <span className="lg:hidden">All</span>
                    <span className="hidden lg:inline">All entities</span>
                  </td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right text-slate-900 tabular-nums">{num(total.assets)}</td>
                  <td className="px-2 sm:px-4 py-3 text-right text-slate-900 tabular-nums whitespace-nowrap text-[13px] sm:text-sm">{formatIDR(total.cost)}</td>
                  <td className="px-2 sm:px-4 py-3 text-right text-slate-900 tabular-nums whitespace-nowrap text-[13px] sm:text-sm">{formatIDR(total.bookValue)}</td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right text-slate-600 tabular-nums">{pct(total.depreciatedPct)}</td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right text-slate-900 tabular-nums">{num(total.underInstallment)}</td>
                  <td className="px-2 sm:px-4 py-3 hidden xl:table-cell text-right text-slate-900 tabular-nums">{num(total.flagged)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* What these totals do not cover */}
      <div className="border-t border-slate-200 px-5 py-2.5 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
        <span>Includes retired, lost and rented / leased assets.</span>
        {nv.count > 0 ? (
          <Link
            to={listLink("", period, { notValued: "true" })}
            className="text-slate-500 hover:text-slate-800 underline underline-offset-2"
            title={[
              nv.missingDate && `${nv.missingDate} without acquisition date`,
              nv.missingPrice && `${nv.missingPrice} without acquisition price`,
              nv.missingLife && `${nv.missingLife} without useful life`,
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            {num(nv.count)} {nv.count === 1 ? "asset" : "assets"} not valued
            {completeness.priceNotInBookValue > 0 &&
              ` — ${formatIDR(completeness.priceNotInBookValue)} of recorded purchase price is not in book value`}
          </Link>
        ) : (
          <span>Every asset is valued.</span>
        )}
      </div>
    </div>
  );
}

// Colours for the category bar: the one data hue and its tints for the largest,
// greys for the rest. A bar this long needs more than one tone to be read.
const CATEGORY_COLORS = ["bg-[#1d5fbf]", "bg-[#4f86d1]", "bg-[#8fb0e0]", "bg-slate-500", "bg-slate-400"];
const OTHER_COLOR = "bg-slate-300";
const TOP_CATEGORIES = 5;

function TrendCard({ data }) {
  const { months, now } = data.trend;
  return (
    <div className="bg-white border border-slate-200 rounded-xl lg:col-span-2 flex flex-col">
      <div className="px-5 pt-4 pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="text-sm font-semibold text-slate-900">Value over time</h2>
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <li className="flex items-center gap-1.5">
              <span className="h-0.5 w-4 bg-[#1d5fbf]" /> Book value
            </li>
            <li className="flex items-center gap-1.5">
              <span className="h-0.5 w-4 bg-slate-400" /> Acquisition cost
            </li>
            <li className="flex items-center gap-1.5">
              <span className="w-4 border-t-2 border-dashed border-slate-500" /> Forecast
            </li>
          </ul>
        </div>
      </div>
      <div className="px-3 pb-2">
        <TrendChart months={months} now={now} />
      </div>
      <p className="mt-auto border-t border-slate-200 px-5 py-2.5 text-xs text-slate-400">
        Month-ends, all entities. After this month it is a forecast: today's assets depreciating on schedule, with no
        new purchases and no disposals. Includes retired, lost and rented / leased assets.
      </p>
    </div>
  );
}

function CategoryCard({ data, period }) {
  const navigate = useNavigate();
  const rows = data.byCategory;
  const total = data.total.bookValue;
  const asOf = utc(data.asOf, { day: "2-digit", month: "short", year: "numeric" });

  // The five largest get their own segment; the rest are one "Other" you cannot open
  const head = rows.slice(0, TOP_CATEGORIES);
  const tail = rows.slice(TOP_CATEGORIES);
  const items = head.map((c, i) => ({
    key: c.categoryId ?? "none",
    label: c.name,
    value: c.bookValue,
    color: CATEGORY_COLORS[i],
    selectable: !!c.categoryId,
    categoryId: c.categoryId,
  }));
  if (tail.length) {
    items.push({
      key: "other",
      label: `Other (${tail.length} ${tail.length === 1 ? "category" : "categories"})`,
      value: tail.reduce((s, c) => s + c.bookValue, 0),
      color: OTHER_COLOR,
      selectable: false,
    });
  }

  const open = (key) => {
    const item = items.find((i) => i.key === key);
    if (!item?.categoryId) return;
    const p = new URLSearchParams({ categoryId: item.categoryId });
    if (period && period !== currentPeriod()) p.set("period", period);
    navigate(`/assets?${p.toString()}`);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl flex flex-col">
      <div className="px-5 pt-4 pb-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4">
          <h2 className="text-sm font-semibold text-slate-900">Book value by category</h2>
          <span className="text-xs text-slate-400">as of {asOf}</span>
        </div>
        <p className="mt-2 text-2xl leading-9 font-semibold text-slate-900 tabular-nums">{formatIDR(total)}</p>
      </div>
      <div className="px-5 pb-4">
        {items.length === 0 ? (
          <p className="py-6 text-sm text-slate-400 text-center">No book value yet.</p>
        ) : (
          <StackedBreakdown
            items={items}
            total={total}
            onSelect={open}
            formatValue={(n) => formatIDR(n)}
            actionHint="Open these assets"
          />
        )}
      </div>
      <p className="mt-auto border-t border-slate-200 px-5 py-2.5 text-xs text-slate-400">
        Click a category to open its assets.
      </p>
    </div>
  );
}

function InstallmentsCard({ data, period }) {
  const { running, months } = data.installments;
  const max = Math.max(1, ...months.map((m) => m.count));
  const half = Math.ceil(months.length / 2);

  const MonthRow = ({ m }) => (
    <li className="flex items-center gap-3 py-1.5">
      <span className="w-[4.75rem] shrink-0 text-sm text-slate-600 tabular-nums whitespace-nowrap">{formatMonth(m.month)}</span>
      <span className="flex-1 min-w-12" aria-hidden="true">
        <span
          className={cn("block h-1.5 rounded-full", m.count ? "bg-[#1d5fbf]" : "bg-transparent")}
          style={{ width: `${(m.count / max) * 100}%` }}
        />
      </span>
      <span className={cn("w-6 text-right text-sm tabular-nums", m.count ? "text-slate-900 font-medium" : "text-slate-300")}>
        {m.count}
      </span>
      <span
        className="w-28 text-right text-sm tabular-nums text-slate-600 whitespace-nowrap"
        title={m.withoutAmount ? `${m.withoutAmount} of ${m.count} without a monthly amount recorded, so this is a minimum` : undefined}
      >
        {m.count === 0 ? (
          <Zero>—</Zero>
        ) : (
          <>
            {m.monthlyAmount > 0 ? (
              // a minimum when some plans in the month have no amount recorded
              `${m.withoutAmount > 0 ? "≥ " : ""}${formatIDR(m.monthlyAmount)}`
            ) : (
              <span className="text-slate-400">no amount</span>
            )}
          </>
        )}
      </span>
    </li>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 px-5 pt-4 pb-3">
        <h2 className="text-sm font-semibold text-slate-900">Installments ending</h2>
        <span className="text-xs text-slate-400">next 12 months · monthly amount that stops</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] border-t border-slate-200">
        <div className="px-5 py-4 space-y-4 lg:border-r border-slate-200">
          <div>
            <p className="text-sm text-slate-500">Running now</p>
            <p className="mt-1 text-2xl leading-9 font-semibold text-slate-900 tabular-nums">{num(running.count)}</p>
            <p className="mt-0.5 text-xs text-slate-400">
              {running.count === 1 ? "installment plan" : "installment plans"} ·{" "}
              <Link
                to={listLink("", period, { ownership: "UNDER_INSTALLMENT" })}
                className="underline underline-offset-2 hover:text-slate-700"
              >
                show assets
              </Link>
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Paid every month</p>
            <p className="mt-1 text-2xl leading-9 font-semibold text-slate-900 tabular-nums">
              {formatIDR(running.monthlyAmount)}
            </p>
            <p className="mt-0.5 text-xs text-slate-400">
              {running.withoutAmount > 0
                ? `${num(running.withoutAmount)} ${running.withoutAmount === 1 ? "plan has" : "plans have"} no monthly amount recorded, so this is a minimum`
                : "across all running plans"}
            </p>
          </div>
        </div>

        <div className="px-5 py-3 grid grid-cols-1 xl:grid-cols-2 xl:gap-x-8">
          <ul>
            {months.slice(0, half).map((m) => (
              <MonthRow key={m.month} m={m} />
            ))}
          </ul>
          <ul>
            {months.slice(half).map((m) => (
              <MonthRow key={m.month} m={m} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const allowed = CAN_SEE_FINANCE.includes(user?.role);
  const period = searchParams.get("period") || "";
  const { data, isLoading, isError } = useFinanceOverview({ period, enabled: allowed });

  if (!allowed) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-700 font-medium">Finance is restricted</p>
        <p className="text-slate-500 text-sm mt-1">Only financial roles can see this page.</p>
      </div>
    );
  }

  function setPeriod(v) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // blank or back to this month = the default, kept out of the URL
      if (v && v !== currentPeriod()) next.set("period", v);
      else next.delete("period");
      return next;
    });
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Finance</h1>
          {data && finding(data) && <p className="text-sm text-slate-500 mt-0.5">{finding(data)}</p>}
        </div>
        <input
          type="month"
          aria-label="Period"
          title="Month for acquisition cost, book value and depreciated"
          value={period || currentPeriod()}
          max={currentPeriod()}
          onChange={(e) => setPeriod(e.target.value)}
          className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        />
      </div>

      {isError ? (
        <div className="text-center py-20 text-rose-600 text-sm">
          Could not load the finance overview. Please refresh.
        </div>
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          <EntitiesCard data={data} period={period} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <TrendCard data={data} />
            <CategoryCard data={data} period={period} />
          </div>
          <InstallmentsCard data={data} period={period} />
        </div>
      )}
    </div>
  );
}
